/**
 * Data Transformers: User Generated Content
 *
 * Transforms data from Stream Feeds activities to formats expected by UI components.
 * Handles conversion between Stream Feeds activity format and post/comment formats.
 */

import { decodeHtmlEntities } from './htmlEntities';
import { normalizeLocationAttachment } from './locationDisplay';

// Transform Stream Feeds activity to post format for display
export const transformStreamActivityToPost = activity => {
  if (!activity) return null;
  // Stream Feeds activity structure:
  // {
  //   id: "activity-id",
  //   actor: "user:userId",
  //   verb: "post",
  //   object: "post:timestamp",
  //   time: "2025-10-21T...",
  //   content: "post text",
  //   image: "url",
  //   crew_id: "12345",
  //   author_name: "John Doe",
  //   ... other custom fields
  //   reaction_counts: { like: 5, love: 2 }
  //   own_reactions: { like: [...], love: [...] }
  // }

  const rawActorId =
    activity.authorId ||
    activity.author_id ||
    activity.user_id ||
    (typeof activity.actor === 'object' ? activity.actor?.id : activity.actor);
  const actorId = rawActorId ? String(rawActorId).replace('user:', '') : null;

  const transformedPost = {
    id: activity.id,
    author: actorId,
    authorId: actorId,
    authorName: activity.author_name || activity.authorName || 'Anonymous',
    authorHandle: activity.author_handle || activity.authorHandle || null,
    authorAvatar: activity.author_avatar || activity.authorAvatar || null,
    accountStatus: activity.account_status || activity.accountStatus || null,
    timeAgo: formatTimeAgo(activity.time),
    content: decodeHtmlEntities(activity.content || ''),
    image: activity.image ? decodeHtmlEntities(activity.image) : null,
    images: (activity.all_images || (activity.image ? [activity.image] : [])).map(
      decodeHtmlEntities
    ),
    gifAttachment: activity.gif_attachment || activity.gif || null,
    locationAttachment: normalizeLocationAttachment(activity.location_attachment),
    pollAttachment: activity.poll_attachment || null,
    likes: activity.reaction_counts?.like || 0,
    loves: activity.reaction_counts?.love || 0,
    fires: activity.reaction_counts?.fire || 0,
    comments: [], // Comments will be fetched separately from Stream Feeds reactions
    commentCount: activity.reaction_counts?.comment || 0, // Add comment count from Stream Feeds
    repostCount: activity.reaction_counts?.repost || 0,
    verified: activity.verified || false,
    crewId: activity.crew_id,
    crewName: activity.crew_name || '',
    contentType: 'post',
    createdAt: activity.time,
    // Verb – 'post' or 'repost'
    verb: activity.verb || 'post',
    // Quoted post (for reposts) — hydrate with computed timeAgo
    quotedPost: activity.quoted_post
      ? {
          id: activity.quoted_post.id,
          content: decodeHtmlEntities(activity.quoted_post.content || ''),
          authorId: activity.quoted_post.authorId || activity.quoted_post.author,
          authorName:
            activity.quoted_post.authorName || activity.quoted_post.author_name || 'Anonymous',
          authorHandle:
            activity.quoted_post.authorHandle || activity.quoted_post.author_handle || null,
          authorAvatar:
            activity.quoted_post.authorAvatar || activity.quoted_post.author_avatar || null,
          accountStatus:
            activity.quoted_post.accountStatus || activity.quoted_post.account_status || null,
          images: (
            activity.quoted_post.images ||
            (activity.quoted_post.image ? [activity.quoted_post.image] : [])
          ).map(decodeHtmlEntities),
          image: decodeHtmlEntities(
            activity.quoted_post.image ||
              (activity.quoted_post.images && activity.quoted_post.images[0]) ||
              null
          ),
          crewId: activity.quoted_post.crewId || activity.quoted_post.crew_id,
          crewName: activity.quoted_post.crewName || activity.quoted_post.crew_name || '',
          timeAgo:
            activity.quoted_post.timeAgo ||
            formatTimeAgo(activity.quoted_post.createdAt || activity.quoted_post.time),
          createdAt: activity.quoted_post.createdAt || activity.quoted_post.time,
        }
      : null,
    originalActivityId: activity.original_activity_id || null,
    // Stream Feeds specific
    activityId: activity.id,
    foreignId: activity.foreign_id || null,
    reactionCounts: activity.reaction_counts || {},
    ownReactions: activity.own_reactions || {},
    // Check if current user has liked
    likedByUser: activity.own_reactions?.like && activity.own_reactions.like.length > 0,
    metadata: activity,
  };
  return transformedPost;
};

// Transform array of Stream Feeds activities to posts
export const transformStreamActivitiesToPosts = activities => {
  if (!Array.isArray(activities)) return [];
  return activities.map(transformStreamActivityToPost).filter(Boolean);
};

// Transform UserGeneratedContent to post format for display (LEGACY - for backward compatibility)
export const transformContentToPost = contentItem => {
  if (!contentItem) return null;

  const content = contentItem.Content || {};
  const metadata = content.metadata || {};

  return {
    id: contentItem.id,
    author: metadata.created_by || contentItem.user_id,
    authorName: metadata.author_name || 'Anonymous',
    timeAgo: formatTimeAgo(contentItem.created_at),
    content: content.text || '',
    image: content.images?.[0] || null,
    images: content.images || [],
    likes: content.likes || 0,
    comments: content.comments || [],
    verified: metadata.verified || false,
    crewId: contentItem.crew_id,
    crewName: metadata.crew_name || '',
    contentType: contentItem.content_type,
    createdAt: contentItem.created_at,
    metadata: metadata,
  };
};

// Transform array of UserGeneratedContent to posts
export const transformContentToPosts = contentArray => {
  if (!Array.isArray(contentArray)) return [];
  return contentArray.map(transformContentToPost).filter(Boolean);
};

// Transform UserGeneratedContent to comment format
export const transformContentToComment = contentItem => {
  if (!contentItem) return null;

  const content = contentItem.Content || {};
  const metadata = content.metadata || {};

  return {
    id: contentItem.id,
    userId: contentItem.user_id,
    userName: metadata.author_name || 'Anonymous',
    text: content.text || '',
    createdAt: contentItem.created_at,
    timeAgo: formatTimeAgo(contentItem.created_at),
    likes: content.likes || 0,
    replies: content.replies || [],
    metadata: metadata,
  };
};

// Transform UserGeneratedContent to activity format (for MyActivityPage)
export const transformContentToActivity = contentItem => {
  if (!contentItem) return null;

  const content = contentItem.Content || {};
  const metadata = content.metadata || {};

  return {
    id: contentItem.id,
    type: contentItem.content_type,
    author: metadata.created_by || contentItem.user_id,
    authorName: metadata.author_name || 'Anonymous',
    timeAgo: formatTimeAgo(contentItem.created_at),
    content: content.text || '',
    image: content.images?.[0] || null,
    images: content.images || [],
    likes: content.likes || 0,
    comments: content.comments || [],
    verified: metadata.verified || false,
    crewId: contentItem.crew_id,
    crewName: metadata.crew_name || '',
    createdAt: contentItem.created_at,
    metadata: metadata,
  };
};

// Transform array of UserGeneratedContent to activities
export const transformContentToActivities = contentArray => {
  if (!Array.isArray(contentArray)) return [];
  return contentArray.map(transformContentToActivity).filter(Boolean);
};

// Helper function to format time ago
const formatTimeAgo = dateString => {
  if (!dateString) return 'Unknown time';

  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString();
  }
};

// Transform post data to UserGeneratedContent format for creation
export const transformPostToContent = postData => {
  const { text, images = [], crewId, contentType = 'post', metadata = {} } = postData;

  return {
    content_type: contentType,
    crew_id: crewId,
    content_data: {
      text: text || '',
      images: images,
      likes: 0,
      comments: [],
      metadata: {
        ...metadata,
        created_at: new Date().toISOString(),
      },
    },
  };
};

// Transform comment data to UserGeneratedContent format for creation
export const transformCommentToContent = commentData => {
  const { text, parentContentId, crewId, metadata = {} } = commentData;

  return {
    content_type: 'comment',
    crew_id: crewId,
    content_data: {
      text: text || '',
      images: [],
      likes: 0,
      comments: [],
      parent_content_id: parentContentId,
      metadata: {
        ...metadata,
        created_at: new Date().toISOString(),
      },
    },
  };
};

// Helper function to extract user information from content
export const extractUserFromContent = contentItem => {
  if (!contentItem) return null;

  const metadata = contentItem.Content?.metadata || {};

  return {
    id: contentItem.user_id,
    name: metadata.author_name || 'Anonymous',
    verified: metadata.verified || false,
    avatar: metadata.avatar_url || null,
  };
};

// Helper function to extract crew information from content
export const extractCrewFromContent = contentItem => {
  if (!contentItem) return null;

  const metadata = contentItem.Content?.metadata || {};

  return {
    id: contentItem.crew_id,
    name: metadata.crew_name || 'Unknown Crew',
    category: metadata.crew_category || null,
  };
};

// Helper function to group content by crew
export const groupContentByCrew = contentArray => {
  if (!Array.isArray(contentArray)) return {};

  return contentArray.reduce((groups, content) => {
    const crewId = content.crew_id;
    if (!groups[crewId]) {
      groups[crewId] = [];
    }
    groups[crewId].push(content);
    return groups;
  }, {});
};

// Helper function to sort content by date (newest first)
export const sortContentByDate = (contentArray, ascending = false) => {
  if (!Array.isArray(contentArray)) return [];

  return [...contentArray].sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return ascending ? dateA - dateB : dateB - dateA;
  });
};

// Helper function to filter content by type
export const filterContentByType = (contentArray, contentType) => {
  if (!Array.isArray(contentArray)) return [];
  return contentArray.filter(content => content.content_type === contentType);
};

// Helper function to get content statistics
export const getContentStats = contentArray => {
  if (!Array.isArray(contentArray)) {
    return { total: 0, posts: 0, comments: 0, totalLikes: 0, totalComments: 0 };
  }

  const stats = contentArray.reduce(
    (acc, content) => {
      acc.total += 1;

      if (content.content_type === 'post') {
        acc.posts += 1;
      } else if (content.content_type === 'comment') {
        acc.comments += 1;
      }

      const contentData = content.Content || {};
      acc.totalLikes += contentData.likes || 0;
      acc.totalComments += (contentData.comments || []).length;

      return acc;
    },
    { total: 0, posts: 0, comments: 0, totalLikes: 0, totalComments: 0 }
  );

  return stats;
};
